import React, { useState, useEffect } from 'react';
import moment from 'moment';

import API_URL from '../config/api';

const Display = () => {
  const [data, setData] = useState({
    messages: [],
    events: [],
    pages: [],
    settings: {}
  });
  const [currentTime, setCurrentTime] = useState(moment());
  const [tickerOffset, setTickerOffset] = useState(0);

  useEffect(() => {
    fetchDisplayData();
    
    // Auto-refresh every 10 seconds
    const refreshInterval = setInterval(fetchDisplayData, 10000);
    
    // Update clock every second
    const clockInterval = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    // Ticker animation
    const tickerInterval = setInterval(() => {
      setTickerOffset(prev => prev - 1);
    }, 50); // Adjust speed here (lower = faster)

    return () => {
      clearInterval(refreshInterval);
      clearInterval(clockInterval);
      clearInterval(tickerInterval);
    };
  }, []);

  const fetchDisplayData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/message-board/display`);
      if (response.ok) {
        const displayData = await response.json();
        setData(displayData);
      }
    } catch (error) {
      console.error('Error fetching display data:', error);
    }
  };

  // Find highest priority page or message
  const emergencyPage = data.pages?.find(p => p.priority === 'emergency');
  const urgentPage = data.pages?.find(p => p.priority === 'urgent');
  const activePage = emergencyPage || urgentPage || data.pages?.[0];
  
  const emergencyMessage = data.messages?.find(m => m.category === 'emergency' || m.priority === 4);

  // Prepare ticker messages
  const tickerMessages = [
    ...data.messages.map(m => `📰 ${m.content}`),
    ...data.events.slice(0, 5).map(e => 
      `📅 ${e.title} - ${moment(e.event_date).format('MMM D')}${e.event_time ? ` at ${moment(e.event_time, 'HH:mm:ss').format('h:mm A')}` : ''}`
    )
  ];

  const tickerContent = tickerMessages.join(' │ ') + ' │ ';

  // Emergency mode - full screen takeover
  if (emergencyMessage) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        animation: 'pulse 2s ease-in-out infinite'
      }}>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.8; }
          }
          @keyframes flash-border {
            0%, 100% { border-color: #fef2f2; }
            50% { border-color: #dc2626; }
          }
        `}</style>
        
        <div style={{
          fontSize: 120,
          marginBottom: 32,
          animation: 'pulse 1s ease-in-out infinite'
        }}>
          🚨
        </div>
        
        <div style={{
          fontSize: 64,
          fontWeight: 700,
          marginBottom: 24,
          textAlign: 'center',
          textTransform: 'uppercase',
          letterSpacing: 4
        }}>
          EMERGENCY ALERT
        </div>
        
        <div style={{
          fontSize: 48,
          fontWeight: 500,
          textAlign: 'center',
          maxWidth: '80%',
          lineHeight: 1.4,
          padding: 40,
          background: 'rgba(0,0,0,0.3)',
          borderRadius: 16,
          border: '4px solid white',
          animation: 'flash-border 1s ease-in-out infinite'
        }}>
          {emergencyMessage.content}
        </div>
      </div>
    );
  }

  // Page mode - prominent employee paging
  if (activePage) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: activePage.priority === 'emergency' 
          ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
          : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        position: 'relative'
      }}>
        <style>{`
          @keyframes flash-border {
            0%, 100% { border-color: white; }
            50% { border-color: transparent; }
          }
        `}</style>

        {/* Flashing border */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: '8px solid white',
          animation: 'flash-border 1s ease-in-out infinite',
          pointerEvents: 'none'
        }} />
        
        <div style={{
          fontSize: 100,
          marginBottom: 40
        }}>
          📢
        </div>
        
        <div style={{
          fontSize: 48,
          fontWeight: 600,
          marginBottom: 16,
          textTransform: 'uppercase',
          letterSpacing: 2
        }}>
          Attention Please
        </div>
        
        <div style={{
          fontSize: 80,
          fontWeight: 700,
          marginBottom: 40,
          textAlign: 'center'
        }}>
          {activePage.employee_name}
        </div>
        
        <div style={{
          fontSize: 48,
          fontWeight: 500,
          textAlign: 'center',
          maxWidth: '80%',
          lineHeight: 1.4,
          padding: 40,
          background: 'rgba(0,0,0,0.2)',
          borderRadius: 16
        }}>
          {activePage.message}
        </div>

        {activePage.page_type === 'video' && (
          <div style={{
            marginTop: 40,
            fontSize: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 16
          }}>
            📹 Live Video Feed
          </div>
        )}
      </div>
    );
  }

  // Normal mode - ticker + events
  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      color: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255,255,255,0.1)',
        padding: '24px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backdropFilter: 'blur(10px)'
      }}>
        <div>
          <div style={{ fontSize: 36, fontWeight: 700, marginBottom: 4 }}>
            SVT CONSULTING
          </div>
          <div style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }}>
            Employee Information Board
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 48, fontWeight: 700 }}>
            {currentTime.format('h:mm')}
          </div>
          <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.7)' }}>
            {currentTime.format('A • dddd, MMM D')}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: 40, overflowY: 'auto' }}>
        {/* Today's Events */}
        {data.events && data.events.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <h2 style={{ 
              fontSize: 32, 
              fontWeight: 600, 
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              📅 Today's Schedule
            </h2>
            
            <div style={{ display: 'grid', gap: 20 }}>
              {data.events.slice(0, 5).map((event, index) => (
                <div 
                  key={index}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    padding: 24,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 24,
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}
                >
                  {/* Date Badge */}
                  <div style={{
                    width: 100,
                    height: 100,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <div style={{ fontSize: 36, fontWeight: 700 }}>
                      {moment(event.event_date).format('DD')}
                    </div>
                    <div style={{ fontSize: 16 }}>
                      {moment(event.event_date).format('MMM')}
                    </div>
                  </div>

                  {/* Event Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>
                      {event.title}
                    </div>
                    {event.event_time && (
                      <div style={{ fontSize: 24, color: '#3b82f6', marginBottom: 8 }}>
                        🕐 {moment(event.event_time, 'HH:mm:ss').format('h:mm A')}
                      </div>
                    )}
                    {event.description && (
                      <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.7)' }}>
                        {event.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Announcements */}
        {data.messages && data.messages.filter(m => m.category !== 'emergency' && m.priority !== 4).length > 0 && (
          <div>
            <h2 style={{ 
              fontSize: 32, 
              fontWeight: 600, 
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              📰 Announcements
            </h2>
            
            <div style={{ display: 'grid', gap: 20 }}>
              {data.messages
                .filter(m => m.category !== 'emergency' && m.priority !== 4)
                .slice(0, 3)
                .map((message, index) => (
                  <div 
                    key={index}
                    style={{
                      background: message.priority >= 3 
                        ? 'rgba(239, 68, 68, 0.2)'
                        : 'rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(10px)',
                      padding: 24,
                      borderRadius: 12,
                      border: message.priority >= 3 
                        ? '2px solid #ef4444'
                        : '1px solid rgba(255,255,255,0.2)'
                    }}
                  >
                    <div style={{ 
                      fontSize: 24, 
                      fontWeight: 600, 
                      marginBottom: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12
                    }}>
                      {message.priority >= 3 && '⚠️'}
                      {message.title}
                    </div>
                    <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.9)' }}>
                      {message.content}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!data.events || data.events.length === 0) && 
         (!data.messages || data.messages.filter(m => m.category !== 'emergency').length === 0) && (
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.5)'
          }}>
            <div style={{ fontSize: 80, marginBottom: 24 }}>📋</div>
            <div style={{ fontSize: 32 }}>No scheduled events or announcements</div>
            <div style={{ fontSize: 24, marginTop: 8 }}>Have a great day!</div>
          </div>
        )}
      </div>

      {/* Scrolling Ticker */}
      {tickerMessages.length > 0 && (
        <div style={{
          background: 'rgba(59, 130, 246, 0.9)',
          backdropFilter: 'blur(10px)',
          padding: '16px 0',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          borderTop: '2px solid rgba(255,255,255,0.3)'
        }}>
          <div style={{
            fontSize: 24,
            fontWeight: 500,
            display: 'inline-block',
            paddingLeft: '100%',
            animation: 'scroll 60s linear infinite',
            transform: `translateX(${tickerOffset}px)`
          }}>
            {tickerContent.repeat(10)}
          </div>
          <style>{`
            @keyframes scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default Display;
